import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { LayoutDashboard, TrendingUp, Clock, ShieldCheck, Zap, FolderOpen, CheckCircle2, Target, PartyPopper, UserCheck, DraftingCompass, Users as UsersCode } from "lucide-react";
import { cn } from "../../utils/cn";
import { LanePill } from "../ui/LanePill";
import { scoreEpic } from "../../utils/scoring";
import { Epic, Session, Project, User } from "../../types";
import { calculateProjectRoleCounts } from "../../utils/teamMetrics";

interface KPICardProps {
    icon: React.ReactNode;
    label: string;
    value: string | number;
    sublabel?: string;
    color: string;
    trend?: number;
}

/**
 * KPICard - Internal component for dashboard metrics
 */
function KPICard({ icon, label, value, sublabel, color, trend }: KPICardProps) {
    return (
        <div className="group relative overflow-hidden rounded-lg md:rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider p-3 md:p-5 transition-all duration-200 hover:shadow-siemens hover:border-siemens-petrol/30">
            <div className={cn("absolute top-0 left-0 right-0 h-1 bg-gradient-to-r", color)} />
            <div className={cn("w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center mb-2 md:mb-4", color.replace('from-', 'bg-').split(' ')[0] + '/10')}>
                {icon}
            </div>
            <div className="flex items-baseline gap-1 md:gap-2">
                <span className="text-xl md:text-3xl font-black text-siemens-deep-blue dark:text-white tabular-nums">{value}</span>
                {trend && (
                    <span className={cn("text-[10px] md:text-xs font-bold", trend > 0 ? "text-siemens-feedback-green" : "text-siemens-feedback-red")}>
                        {trend > 0 ? "↑" : "↓"} {Math.abs(trend)}%
                    </span>
                )}
            </div>
            <div className="mt-0.5 md:mt-1">
                <span className="text-xs md:text-sm font-semibold text-siemens-deep-blue-80 dark:text-siemens-text-soft block truncate">{label}</span>
                {sublabel && <span className="text-[10px] md:text-xs text-siemens-text-soft block truncate">{sublabel}</span>}
            </div>
        </div>
    );
}

interface DashboardViewProps {
    epics: Epic[];
    sessions: Session[];
    projects: Project[];
    users: User[];
}

interface ProjectStats {
    id: string;
    name: string;
    count: number;
    totalWsjf: number;
    sgmCount: number;
    nowCount: number;
    doneCount: number;
    avgProgress: number;
    pmCount: number;
    ownerCount: number;
    architectCount: number;
}

/**
 * DashboardView - Portfolio overview with KPIs and lane distribution
 */
export function DashboardView({ epics, projects, users }: DashboardViewProps) {
    const stats = useMemo(() => {
        const scores = epics.map((e) => {
            if ((!e.wsjf || e.wsjf === 0) && (e.business || e.time)) {
                const { wsjf } = scoreEpic(e);
                return wsjf;
            }
            return e.wsjf || 0;
        });
        const sum = scores.reduce((a, b) => a + b, 0);
        const avg = sum / scores.length || 0;
        const maxWsjf = Math.max(...scores, 0);
        const minWsjf = Math.min(...scores.filter(s => s > 0), 0);

        // Lane counts including Done
        const laneCounts: Record<string, number> = { Now: 0, Next: 0, Later: 0, Unassigned: 0, Done: 0 };
        epics.forEach((e) => (laneCounts[e.lane] = (laneCounts[e.lane] || 0) + 1));

        const sgmCount = epics.filter(e => e.sgm).length;
        const totalCod = epics.reduce((a, e) => a + ((e as any).cod || 0), 0);

        // Portfolio progress (avg of all epic progress)
        const totalProgress = epics.reduce((sum, e) => sum + (e.progress || 0), 0);
        const avgProgress = epics.length > 0 ? Math.round(totalProgress / epics.length) : 0;

        // Recently completed epics (completed in last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentlyCompleted = epics
            .filter(e => e.lane === 'Done' && e.completedAt && new Date(e.completedAt) >= thirtyDaysAgo)
            .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime());

        // Global unique role counts - only count IDs that match valid users
        const validUserIds = new Set(users.map(u => u.id));
        const allPms = new Set<string>();
        const allOwners = new Set<string>();
        const allArchitects = new Set<string>();

        projects.forEach(p => {
            if (p.pm) {
                const pmArray = Array.isArray(p.pm) ? p.pm : [p.pm];
                pmArray.forEach(id => {
                    if (validUserIds.has(id)) allPms.add(id);
                });
            }
        });

        epics.forEach(e => {
            if (e.so) {
                const soArray = Array.isArray(e.so) ? e.so : [e.so];
                soArray.forEach(id => {
                    if (validUserIds.has(id)) allOwners.add(id);
                });
            }
            if (e.arq) {
                const arqArray = Array.isArray(e.arq) ? e.arq as string[] : [e.arq];
                arqArray.forEach(id => {
                    if (validUserIds.has(id)) allArchitects.add(id);
                });
            }
        });

        const projectStats: Record<string, ProjectStats> = {};

        // Group epics by project
        const epicsByProject: Record<string, Epic[]> = {};
        epics.forEach(e => {
            const pid = e.projectId || 'project-legacy';
            if (!epicsByProject[pid]) epicsByProject[pid] = [];
            epicsByProject[pid].push(e);
        });

        // Calculate per-project stats
        Object.keys(epicsByProject).forEach(pid => {
            const project = projects?.find?.(p => String(p.id) === String(pid));

            // Skip ghost projects if needed, but here we process what we have
            if (!project && pid !== 'project-legacy') return;

            const projectEpics = epicsByProject[pid];
            const name = project ? project.name : (pid === 'project-legacy' ? 'Legacy Improvements' : pid);

            // Calculate role counts for this project using utility
            const counts = calculateProjectRoleCounts(projectEpics, project, users);

            let totalWsjf = 0;
            let sgmCount = 0;
            let nowCount = 0;
            let doneCount = 0;
            let totalProg = 0;

            projectEpics.forEach(e => {
                let s = e.wsjf || 0;
                if (s === 0 && (e.business || e.time)) s = scoreEpic(e).wsjf;
                totalWsjf += s;
                if (e.sgm) sgmCount++;
                if (e.lane === 'Now') nowCount++;
                if (e.lane === 'Done') doneCount++;
                totalProg += (e.progress || 0);
            });

            projectStats[pid] = {
                id: pid,
                name,
                count: projectEpics.length,
                totalWsjf,
                sgmCount,
                nowCount,
                doneCount,
                avgProgress: projectEpics.length > 0 ? Math.round(totalProg / projectEpics.length) : 0,
                pmCount: counts.pmCount,
                ownerCount: counts.ownerCount,
                architectCount: counts.architectCount
            };
        });

        // Add projects with 0 epics if they exist in project list? 
        // Dashboard currently iterates epics to build stats. If we want 0-epic projects, we should iterate projects.
        // But let's stick to existing logic pattern + our enhancments.

        // Active Stats
        const activeEpicCount = epics.filter(e => e.lane === 'Now' || e.lane === 'Next').length;
        const activeProjectIds = new Set(epics.filter(e => (e.lane === 'Now' || e.lane === 'Next') && e.projectId).map(e => e.projectId));
        const activeProjectCount = activeProjectIds.size;

        return {
            total: epics.length,
            avg, maxWsjf, minWsjf,
            laneCounts, sgmCount, totalCod,
            avgProgress,
            activeEpicCount,
            activeProjectCount,
            doneCount: laneCounts.Done,
            recentlyCompleted,
            projectStats: Object.values(projectStats),
            projectCount: Object.keys(projectStats).length,
            roleCounts: {
                pms: allPms.size,
                owners: allOwners.size,
                architects: allArchitects.size
            }
        };
    }, [epics, projects]);

    return (
        <div className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-y-auto h-full">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                <div>
                    <h2 className="text-lg md:text-xl font-bold text-siemens-deep-blue dark:text-siemens-text-standard">Project Overview</h2>
                    <p className="text-xs md:text-sm text-siemens-text-soft">Strategic metrics and KPIs for your epic backlog</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-siemens-text-soft">Last updated:</span>
                    <span className="text-xs font-bold text-siemens-deep-blue dark:text-siemens-text-standard">{new Date().toLocaleDateString()}</span>
                </div>
            </div>

            {/* KPI Grid - Updated with 6 cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                <KPICard
                    icon={<LayoutDashboard className="w-5 h-5 text-siemens-petrol" />}
                    label="Total Epics"
                    value={stats.total}
                    color="from-siemens-petrol to-siemens-petrol-light"
                />
                <KPICard
                    icon={<CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                    label="Completed"
                    value={stats.doneCount}
                    sublabel={`${stats.total > 0 ? Math.round((stats.doneCount / stats.total) * 100) : 0}% of total`}
                    color="from-emerald-500 to-green-600"
                />
                <KPICard
                    icon={<Target className="w-5 h-5 text-siemens-petrol" />}
                    label="Project Progress"
                    value={`${stats.avgProgress}%`}
                    sublabel="Avg completion"
                    color="from-siemens-petrol to-cyan-600"
                />
                <KPICard
                    icon={<TrendingUp className="w-5 h-5 text-siemens-feedback-green" />}
                    label="Avg WSJF"
                    value={stats.avg.toFixed(1)}
                    sublabel={`${stats.minWsjf.toFixed(0)}-${stats.maxWsjf.toFixed(0)}`}
                    color="from-siemens-feedback-green to-siemens-feedback-green/70"
                />
                <KPICard
                    icon={<Clock className="w-5 h-5 text-siemens-feedback-orange" />}
                    label="In Flight"
                    value={stats.laneCounts.Now}
                    sublabel="Now lane"
                    color="from-siemens-feedback-orange to-siemens-feedback-orange/70"
                />
                <KPICard
                    icon={<ShieldCheck className="w-5 h-5 text-siemens-feedback-blue" />}
                    label="SGM Direction"
                    value={stats.sgmCount}
                    sublabel={`${stats.total > 0 ? ((stats.sgmCount / stats.total) * 100).toFixed(0) : 0}%`}
                    color="from-siemens-feedback-blue to-siemens-feedback-blue/70"
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Lane Distribution - Updated with Done */}
                <div className="md:col-span-2 rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider p-4 md:p-6">
                    <h3 className="text-sm font-bold text-siemens-deep-blue dark:text-siemens-text-standard mb-4">Lane Distribution</h3>
                    <div className="flex-1 w-full space-y-3">
                        {[
                            { lane: "Now", label: "Now", color: "bg-siemens-feedback-green", textColor: "text-siemens-feedback-green" },
                            { lane: "Next", label: "Next", color: "bg-siemens-feedback-orange", textColor: "text-siemens-feedback-orange" },
                            { lane: "Later", label: "Later", color: "bg-siemens-feedback-blue", textColor: "text-siemens-feedback-blue" },
                            { lane: "Unassigned", label: "Unassigned", color: "bg-slate-400", textColor: "text-slate-500" },
                            { lane: "Done", label: "Done", color: "bg-emerald-500", textColor: "text-emerald-500" },
                        ].map(({ lane, label, color, textColor }) => {
                            const count = stats.laneCounts[lane];
                            const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
                            return (
                                <div key={lane} className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="font-semibold text-siemens-deep-blue dark:text-siemens-text-standard">{label}</span>
                                        <div className="flex items-center gap-2">
                                            <span className={cn("font-black", textColor)}>{count}</span>
                                            <span className="text-siemens-text-soft">({percentage.toFixed(0)}%)</span>
                                        </div>
                                    </div>
                                    <div className="h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${percentage}%` }}
                                            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                                            className={cn("h-full rounded-full", color)}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider p-6">
                    <h3 className="text-sm font-bold text-siemens-deep-blue dark:text-siemens-text-standard mb-4">Quick Stats</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between py-3 border-b border-siemens-deep-blue/5 dark:border-siemens-divider">
                            <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-siemens-feedback-orange" />
                                <span className="text-sm font-medium text-siemens-deep-blue-80 dark:text-siemens-text-soft">Total CoD Value</span>
                            </div>
                            <span className="text-lg font-black text-siemens-deep-blue dark:text-white">{stats.totalCod.toFixed(0)}</span>
                        </div>
                        <div className="flex items-center justify-between py-3 border-b border-siemens-deep-blue/5 dark:border-siemens-divider">
                            <div className="flex items-center gap-2">
                                <FolderOpen className="w-4 h-4 text-siemens-petrol" />
                                <span className="text-sm font-medium text-siemens-deep-blue-80 dark:text-siemens-text-soft">Active Projects</span>
                            </div>
                            <span className="text-lg font-black text-siemens-deep-blue dark:text-white">{stats.activeProjectCount}</span>
                        </div>

                        {/* New Role Stats */}
                        <div className="flex items-center justify-between py-3 border-b border-siemens-deep-blue/5 dark:border-siemens-divider">
                            <div className="flex items-center gap-2">
                                <UsersCode className="w-4 h-4 text-blue-500" />
                                <span className="text-sm font-medium text-siemens-deep-blue-80 dark:text-siemens-text-soft">Assigned PMs</span>
                            </div>
                            <span className="text-lg font-black text-siemens-deep-blue dark:text-white">{stats.roleCounts.pms}</span>
                        </div>
                        <div className="flex items-center justify-between py-3 border-b border-siemens-deep-blue/5 dark:border-siemens-divider">
                            <div className="flex items-center gap-2">
                                <UserCheck className="w-4 h-4 text-indigo-500" />
                                <span className="text-sm font-medium text-siemens-deep-blue-80 dark:text-siemens-text-soft">Service Owners</span>
                            </div>
                            <span className="text-lg font-black text-siemens-deep-blue dark:text-white">{stats.roleCounts.owners}</span>
                        </div>
                        <div className="flex items-center justify-between py-3 border-b border-siemens-deep-blue/5 dark:border-siemens-divider">
                            <div className="flex items-center gap-2">
                                <DraftingCompass className="w-4 h-4 text-purple-500" />
                                <span className="text-sm font-medium text-siemens-deep-blue-80 dark:text-siemens-text-soft">Architects</span>
                            </div>
                            <span className="text-lg font-black text-siemens-deep-blue dark:text-white">{stats.roleCounts.architects}</span>
                        </div>

                        <div className="flex items-center justify-between py-3">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                <span className="text-sm font-medium text-siemens-deep-blue-80 dark:text-siemens-text-soft">Completed (30d)</span>
                            </div>
                            <span className="text-lg font-black text-emerald-500">{stats.recentlyCompleted.length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recently Completed Section */}
            {stats.recentlyCompleted.length > 0 && (
                <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/10 border border-emerald-200 dark:border-emerald-800/30 overflow-hidden">
                    <div className="px-6 py-4 border-b border-emerald-200 dark:border-emerald-800/30 flex items-center gap-2">
                        <PartyPopper className="w-5 h-5 text-emerald-600" />
                        <h3 className="text-sm font-bold text-emerald-800 dark:text-emerald-300">Recently Completed</h3>
                        <span className="ml-auto text-xs font-medium text-emerald-600 dark:text-emerald-400">Last 30 days</span>
                    </div>
                    <div className="divide-y divide-emerald-200 dark:divide-emerald-800/30">
                        {stats.recentlyCompleted.slice(0, 5).map((epic) => (
                            <div key={epic.id} className="p-4 flex items-center gap-4 hover:bg-emerald-100/50 dark:hover:bg-emerald-900/20 transition-colors">
                                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-emerald-900 dark:text-emerald-200 leading-tight truncate">{epic.title}</p>
                                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                                        Completed {new Date(epic.completedAt!).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className="text-[10px] uppercase font-bold text-emerald-600">WSJF</span>
                                    <span className="text-lg font-black text-emerald-700 dark:text-emerald-300 tabular-nums">
                                        {(epic.wsjf || 0).toFixed(1)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Project Breakdown */}
            {stats.projectStats.length > 0 && (
                <div className="rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-siemens-deep-blue dark:text-siemens-text-standard flex items-center gap-2">
                            <FolderOpen className="w-4 h-4 text-siemens-petrol" />
                            Project Breakdown
                        </h3>
                        <span className="text-xs font-medium text-siemens-text-soft">{stats.projectCount} projects</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {stats.projectStats
                            .sort((a, b) => b.totalWsjf - a.totalWsjf)
                            .map((project, idx) => {
                                const progressPercent = project.count > 0 ? Math.round((project.doneCount / project.count) * 100) : 0;
                                return (
                                    <motion.div
                                        key={project.name}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.1 }}
                                        className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                                    >
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className={cn(
                                                "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                                                idx === 0 ? "bg-siemens-petrol text-white" :
                                                    idx === 1 ? "bg-siemens-petrol/60 text-white" :
                                                        "bg-siemens-petrol/30 text-siemens-petrol"
                                            )}>
                                                {idx + 1}
                                            </span>
                                            <span className="text-sm font-bold text-siemens-deep-blue dark:text-white truncate">{project.name}</span>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mb-3">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-[10px] font-bold uppercase text-siemens-text-soft">Progress</span>
                                                <span className={cn(
                                                    "text-xs font-black",
                                                    progressPercent >= 100 ? "text-emerald-500" :
                                                        progressPercent >= 50 ? "text-siemens-petrol" : "text-slate-500"
                                                )}>{progressPercent}%</span>
                                            </div>
                                            <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${progressPercent}%` }}
                                                    transition={{ duration: 0.6, delay: 0.2 + idx * 0.1 }}
                                                    className={cn(
                                                        "h-full rounded-full",
                                                        progressPercent >= 100 ? "bg-emerald-500" :
                                                            progressPercent >= 50 ? "bg-siemens-petrol" : "bg-amber-500"
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        {/* Role Counts */}
                                        <div className="flex items-center justify-between mb-3 px-1 py-1 bg-white dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-700">
                                            {/* PM */}
                                            <div className="flex items-center gap-1 px-2" title="Project Managers">
                                                <UsersCode className="w-3 h-3 text-blue-500" />
                                                <span className="text-xs font-bold text-siemens-deep-blue dark:text-white">{project.pmCount}</span>
                                            </div>
                                            <div className="w-px h-3 bg-slate-200 dark:bg-slate-700" />
                                            {/* Owner */}
                                            <div className="flex items-center gap-1 px-2" title="Service Owners">
                                                <UserCheck className="w-3 h-3 text-indigo-500" />
                                                <span className="text-xs font-bold text-siemens-deep-blue dark:text-white">{project.ownerCount}</span>
                                            </div>
                                            <div className="w-px h-3 bg-slate-200 dark:bg-slate-700" />
                                            {/* Architect */}
                                            <div className="flex items-center gap-1 px-2" title="Solution Architects">
                                                <DraftingCompass className="w-3 h-3 text-purple-500" />
                                                <span className="text-xs font-bold text-siemens-deep-blue dark:text-white">{project.architectCount}</span>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div className="bg-white dark:bg-slate-900 rounded p-2">
                                                <div className="text-siemens-text-soft">Epics</div>
                                                <div className="text-lg font-black text-siemens-deep-blue dark:text-white">{project.count}</div>
                                            </div>
                                            <div className="bg-white dark:bg-slate-900 rounded p-2">
                                                <div className="text-siemens-text-soft">In Flight</div>
                                                <div className="text-lg font-black text-siemens-feedback-orange">{project.nowCount}</div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* Top Epics Table */}
            <div className="rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider overflow-hidden">
                <div className="px-6 py-4 border-b border-siemens-deep-blue/5 dark:border-siemens-divider">
                    <h3 className="text-sm font-bold text-siemens-deep-blue dark:text-siemens-text-standard">Top 5 Priority Epics</h3>
                </div>
                <div className="divide-y divide-siemens-deep-blue/5 dark:divide-siemens-divider">
                    {epics
                        .filter(e => e.lane !== 'Done')
                        .sort((a, b) => (b.wsjf || 0) - (a.wsjf || 0))
                        .slice(0, 5)
                        .map((epic, idx) => {
                            const project = projects.find(p => p.id === epic.projectId);
                            const ownerId = Array.isArray(epic.so) ? epic.so[0] : epic.so;
                            const owner = users.find(u => u.id === ownerId);

                            return (
                                <div key={epic.id} className="p-4 flex items-start gap-4 hover:bg-siemens-deep-blue-8/50 dark:hover:bg-siemens-elevation-selected/50 transition-colors">
                                    <span className={cn(
                                        "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1",
                                        idx === 0 ? "bg-amber-500 text-white" :
                                            idx === 1 ? "bg-slate-400 text-white" :
                                                idx === 2 ? "bg-amber-700 text-white" :
                                                    "bg-siemens-petrol/10 text-siemens-petrol"
                                    )}>
                                        {idx + 1}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] font-bold text-siemens-petrol dark:text-siemens-petrol-light uppercase tracking-wider bg-siemens-petrol/5 px-1.5 py-0.5 rounded">
                                                {project?.name || 'No Project'}
                                            </span>
                                            {owner && (
                                                <div className="flex items-center gap-1" title={owner.displayName || owner.name}>
                                                    {owner.photoUrl ? (
                                                        <img src={owner.photoUrl} alt={owner.displayName || owner.name} className="w-4 h-4 rounded-full" />
                                                    ) : (
                                                        <div className="w-4 h-4 rounded-full bg-siemens-deep-blue/10 flex items-center justify-center text-[8px] font-bold text-siemens-deep-blue">
                                                            {(owner.displayName || owner.name).charAt(0)}
                                                        </div>
                                                    )}
                                                    <span className="text-[10px] text-siemens-text-soft hidden sm:inline">{owner.displayName || owner.name}</span>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-sm font-bold text-siemens-deep-blue dark:text-siemens-text-standard leading-tight mb-2">
                                            {epic.title}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <LanePill lane={epic.lane} />
                                                {epic.progress !== undefined && epic.progress > 0 && (
                                                    <span className="text-[10px] font-bold text-siemens-petrol bg-siemens-petrol/10 px-2 py-0.5 rounded-full">
                                                        {epic.progress}% done
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className="text-[10px] uppercase font-bold text-siemens-text-soft">WSJF</span>
                                                <span className="text-lg font-black text-siemens-petrol dark:text-siemens-petrol-light tabular-nums">
                                                    {(epic.wsjf || 0).toFixed(1)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
}

export default DashboardView;
